import { SearchType } from "Dashboard/domain/SearchType";
import { IOutputListItemSearch } from "Dashboard/scenes/Stock/domain/IOutputListItemSearch";
import { useAppDispatch } from "hooks/hooks";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetFocus,
  UseFormSetValue
} from "react-hook-form";
import { FormatDateIso } from "utils/dateUtils";
import { setSearchPopover } from "../outputSlice";
import DatePicker from "react-datepicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import classes from "../../../../../../components/Popover/popover.module.scss";
import classesStock from "Dashboard/scenes/Stock/popover.module.scss";
import { styles as stylesPopover } from "components/Popover/popoverSelectStyle";
import RepositorySelect from "../../Input/components/RepositorySelect";
import UnitSelect from "Dashboard/components/UnitSelect";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import TriStateCheckbox from "../../Input/components/TriStateCheckbox";

interface IProps {
  register: UseFormRegister<IOutputListItemSearch>;
  handleSubmit: UseFormHandleSubmit<IOutputListItemSearch>;
  reset: UseFormReset<IOutputListItemSearch>;
  control: Control<IOutputListItemSearch, any>;
  getValues: UseFormGetValues<IOutputListItemSearch>;
  setFocus: UseFormSetFocus<IOutputListItemSearch>;
  searchType: SearchType;
  setSearchType: React.Dispatch<React.SetStateAction<SearchType>>;
  setValue: UseFormSetValue<IOutputListItemSearch>;
  setGetOutputListItemsSearchParams: Dispatch<SetStateAction<IOutputListItemSearch>>;
};

const SearchPopover = (props: IProps) => {
  const dispatch = useAppDispatch();
  const {
    register,
    handleSubmit,
    control,
    getValues,
    setFocus,
    setValue,
    setGetOutputListItemsSearchParams
  } = props;

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
    [getValues("dateFrom") ? new Date(getValues("dateFrom")!) : null,
    getValues("dateTo") ? new Date(getValues("dateTo")!) : null]);

  const close = () => {
    dispatch(setSearchPopover({ isOpen: false }));
  };

  const onSubmit = async (data: IOutputListItemSearch) => {
    try {
      if (props.searchType === SearchType.BASIC) {
        data = { searchQuery: data.searchQuery };
      } else {
        data.searchQuery = undefined;
      }
      setGetOutputListItemsSearchParams(data);
      close();
    } catch (err) {
      console.error(err);
    }
  }

  const switchSearchMode = (searchType: SearchType) => {
    props.setSearchType(searchType);
  };

  useEffect(() => {
    if (props.searchType === SearchType.BASIC) {
      setFocus("searchQuery");
    } else {
      setFocus("code");
    }
  }, [setFocus, props.searchType]);

  return (
    <form
      className={`${classesStock.searchOutputPopover} ${classes.basicPopover}`}
      onSubmit={handleSubmit(onSubmit)}
    >
      {props.searchType === SearchType.BASIC && (
        <>
          <div className={classes.row}>
            <div className={`${classes.col} ${classes.colDir}`}>
              <input
                type="text"
                placeholder=""
                className={`${classes.fullWidth} ${classes.itemBorder}`}
                {...register("searchQuery", { required: false })}
              />
            </div>
            <div className={classes.gPopoverDisclaimer}>
              * možete uneti više reči tj. kriterijuma pretrage
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.buttons}>
              <label
                onClick={() => switchSearchMode(SearchType.ADVANCED)}
                className={classes.clickableText}
              >
                NAPREDNA PRETRAGA
              </label>
              <button className={classes.iconButton} type="submit">
                <FontAwesomeIcon
                  icon={faLongArrowAltDown}
                  className={classes.icon} />
              </button>
            </div>
          </div>
        </>
      )}
      {props.searchType === SearchType.ADVANCED && (
        <>
          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>šifra artikla</label>
              <input type="text" className={classes.itemBorder}
                {...register("code", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>br. otpremnice</label>
              <input type="text" className={classes.itemBorder}
                {...register("billOfLandingIdRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>br. trebovanja</label>
              <input type="text" className={classes.itemBorder}
                {...register("requisitionIdRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>naziv artikla</label>
              <input type="text" className={classes.itemBorder}
                {...register("name", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>magacin</label>
              <Controller
                name="repositoryId"
                control={control}
                defaultValue={undefined}
                rules={{
                  required: false,
                }}
                render={({ field: { onChange } }) => (
                  <RepositorySelect
                    value={getValues("repositoryId")}
                    onChange={onChange}
                    customStyles={stylesPopover.customStyleClassic}
                  />
                )}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>količina iz.</label>
              <input type="text" className={classes.itemBorder}
                {...register("quantityAmountRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>mera</label>
              <Controller
                name="quantityUnit"
                control={control}
                defaultValue={undefined}
                rules={{
                  required: false,
                }}
                render={({ field: { onChange } }) => (
                  <UnitSelect
                    value={getValues("quantityUnit")}
                    onChange={onChange}
                    customStyles={stylesPopover.customStyleClassic}
                  />
                )}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>datum izlaza</label>
              <div className={"datepickerWrapper"}>
                <DatePicker
                  selectsRange={true}
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  showMonthDropdown
                  showYearDropdown
                  onChange={(date: any) => {
                    setDateRange([date[0], date[1]]);
                    setValue("dateFrom", date[0] && FormatDateIso(date[0]));
                    setValue("dateTo", date[1] && FormatDateIso(date[1]));
                  }}
                  isClearable={true}
                  className={`${classes.itemBorder} ${classes.dateItem}`}
                />
              </div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>odvezao i registracija</label>
              <input type="text" className={classes.itemBorder}
                {...register("takenAwayByDriverAndVehicle", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>gde je otišlo</label>
              <input type="text" className={classes.itemBorder}
                {...register("destination", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>dobavljač</label>
              <input type="text" className={classes.itemBorder}
                {...register("supplier", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>napomena</label>
              <input type="text" className={classes.itemBorder}
                {...register("note", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>xxx</label>
              <Controller
                name="xxx"
                control={control}
                render={({ field }) => (
                  <div className={classesStock.triStateCh}>
                    <TriStateCheckbox
                      value={field.value}
                      onChange={field.onChange}
                    />
                    {(field.value !== true && field.value !== false)
                      && <div className={classes.gPopoverDisclaimer}>
                        Bilo koja vrednost kvačice
                      </div>}
                  </div>
                )}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.buttons}>
              <label
                onClick={() => switchSearchMode(SearchType.BASIC)}
                className={classes.clickableText}
              >
                PAMETNA PRETRAGA
              </label>
              <button className={classes.iconButton} type="submit">
                <FontAwesomeIcon
                  icon={faLongArrowAltDown}
                  className={classes.icon} />
              </button>
            </div>
          </div>
        </>)}
    </form>
  );
};

export default SearchPopover;
